import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment'
import classNames from 'classnames'
import shortid from 'shortid'
import { Link } from 'react-router-dom'

import styles from '../component-styles/calendar-list-view.module.scss'
import { Preloader } from './loader'
import '../component-styles/calendar-overwrites.scss'
import { dateFormatShort } from '../core/variables'
import {
  startOfYear,
  // startOfMonth,
  endOfMonth,
  formatForAPI,
  apiDateStringToDate,
  dateToTimeStamp,
} from '../helpers/dates'

import { cap, getAnomalyDateRange, getItemByKeyValue } from '../helpers'

import {
  getCalendarAnomalyRangeAsync,
  getCalendarEventsAsync,
  getCalendarCampaignsAsync,
  getCalendarKpiMetrics,
  getCalendarKpis,
  getCalendarGoalMetrics,
  requestAnomalyGraphData,
  getCurrentKeyMetrics,
} from '../store/actions'
import AnomalyDiff from './anomaly-diff'

export function formatKpi(
  kpi: number,
  currency: string,
  showCurrency?: boolean,
): string {
  const currencySymbol = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'GBP',
  }).formatToParts()[0].value

  if (kpi >= 1000000000) {
    return `${showCurrency ? currencySymbol || '£' : ''}${(
      kpi / 1000000000
    ).toFixed(2)}b`
  }

  if (kpi >= 1000000) {
    return `${showCurrency ? currencySymbol || '£' : ''}${(
      kpi / 1000000
    ).toFixed(2)}m`
  }

  if (kpi >= 1000) {
    return `${showCurrency ? currencySymbol || '£' : ''}${(kpi / 1000).toFixed(
      2,
    )}k`
  }

  return `${showCurrency ? currencySymbol || '£' : ''}${kpi}`
}

// export function convertPercentageToStartFromHalf(percentage: number): number {
//   let fromHalf = percentage
//   fromHalf *= 10000
//   fromHalf /= 2
//   fromHalf += 5000
//   fromHalf /= 10000
//   return fromHalf
// }

export function getDetail(metricID, anomalyData, key) {
  if (anomalyData[metricID] && anomalyData[metricID].anomalyRange.length > 0) {
    const searchDate = moment(key, 'YYYY-MM-DD').format('DD/MM/YYYY')
    const { anomalyRange } = anomalyData[metricID]
    const item = getItemByKeyValue(anomalyRange, 'date', searchDate)
    return item === -1 ? null : item
  }
  return null
}

interface FillEventsParams {
  filters: any
  anomalies: any
  events: any
  campaigns: any
  kpis: any
  selectedKpi: string
  currency: string
  anomalyData: any
}

export function fillEvents({
  filters,
  anomalies,
  events,
  campaigns,
  kpis,
  selectedKpi,
  currency,
  anomalyData,
}: FillEventsParams): any {
  const orderedEvents = {}
  const eventsForRendering: any[] = []
  if (selectedKpi !== 'None' && kpis.size > 0) {
    kpis.forEach((kpi: any, key: string): void => {
      const d = {
        date: key,
        title: formatKpi(kpi.metricValue, currency),
        start: new Date(key),
        type: 'kpi',
        // backgroundColor: 'rgba(0, 125, 155, 0)',
        // textColor: `rgba(0, 125, 155, ${percentage})`,
        // borderColor: 'transparent',
      }
      orderedEvents[key] = orderedEvents[key] ? [...orderedEvents[key], d] : [d]
      // const percentage = convertPercentageToStartFromHalf(kpi.percentage)
      eventsForRendering.push(d)
    })

    return eventsForRendering
  }

  if (filters.campaigns) {
    campaigns.forEach((campaign: any): void => {
      const d = {
        date: campaign.startDate,
        title: `${campaign.campaignTitle}`,
        start: apiDateStringToDate(campaign.startDate),
        end: apiDateStringToDate(campaign.endDate),
        type: 'campaign',
        description: campaign.campaignData,
        // backgroundColor: '#FFD70F',
        // textColor: '#4a4100',
        // borderColor: '#FFD70F',
        // classNames: ['cal-campaign'],
      }

      // const startDate = moment(campaign.startDate)
      // const endDate = moment(campaign.endDate)
      // const days = endDate.diff(startDate, 'days') + 1
      // const date = moment(campaign.startDate)
      // for (let i = 1; i <= days; i += 1) {
      //   const keyDate = date.add(1, 'days').format('YYYYMMDD')
      //   orderedEvents[keyDate] = orderedEvents[keyDate]
      //     ? [...orderedEvents[keyDate], d]
      //     : [d]
      // }
      eventsForRendering.push(d)
      d.title = `Campaign start: ${campaign.campaignTitle}`
      orderedEvents[campaign.startDate] = orderedEvents[campaign.startDate]
        ? [...orderedEvents[campaign.startDate], { ...d }]
        : [{ ...d }]
      d.title = `Campaign end: ${campaign.campaignTitle}`
      orderedEvents[campaign.endDate] = orderedEvents[campaign.endDate]
        ? [...orderedEvents[campaign.endDate], d]
        : [d]
    })
  }

  if (filters.anomalies) {
    // nested loop should be fine because
    // there will never be many in the inner loop, [1-10] realistically
    anomalies.forEach((dayAnomalies: any[], key: string): void => {
      dayAnomalies.forEach(
        ({ displayName, metricID, date }: AnomalyCalendarItem): void => {
          const d = {
            date,
            // apiName,
            // metric,
            metricID,
            title: `${cap(displayName)} Anomaly`,
            start: new Date(key),
            type: 'anomaly',
            // detail: null,
            detail: getDetail(metricID, anomalyData, date),
            // backgroundColor: 'transparent',
            // textColor: '#E61969',
            // borderColor: 'transparent',
          }
          orderedEvents[date] = orderedEvents[date]
            ? [...orderedEvents[date], d]
            : [d]
          eventsForRendering.push(d)
        },
      )
    })
  }

  if (filters.events) {
    // nested loop should be fine because
    // there will never be many in the inner loop, [1-10] realistically
    events.forEach((dayEvents: any[], key: string): void => {
      dayEvents.forEach(({ eventTitle, startDate }: any): void => {
        const d = {
          date: startDate,
          title: eventTitle,
          start: new Date(key),
          type: 'event',
          // backgroundColor: 'transparent',
          // textColor: '#007D9B',
          // borderColor: 'transparent',
        }
        orderedEvents[startDate] = orderedEvents[startDate]
          ? [...orderedEvents[startDate], d]
          : [d]
        eventsForRendering.push(d)
      })
    })
  }
  return orderedEvents
}

export function getNextHeight(height: number, width: number): number {
  if (width > height) {
    return height - 200
  }

  return width - 200
}

interface PropsEventRow {
  items: any[]
}

export function EventRow({ items }: PropsEventRow): React.ReactElement {
  return (
    <span key={shortid.generate()}>
      {items &&
        items.map((item) => {
          const { metricID } = item
          const c = classNames(styles.eventItem, {
            [styles.kpi]: item.type === 'kpi',
            [styles.campaign]: item.type === 'campaign',
            [styles.anomaly]: item.type === 'anomaly',
            [styles.event]: item.type === 'event',
          })

          if (item.type === 'anomaly') {
            const fd = moment(item.date, 'YYYY-MM-DD').format('DD-MM-YYYY')
            return (
              <span key={shortid.generate()}>
                <Link to={`/explain/anomaly/${metricID}/${fd}`}>
                  <span className={c} key={shortid.generate()}>
                    <span className={styles.eventItemTitle}>{item.title}</span>
                  </span>
                </Link>
                {item.detail !== -1 && (
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Difference</th>
                        <th>Actual</th>
                        <th>Expected</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <AnomalyDiff
                            compact
                            actual={item.detail.actual}
                            expected={item.detail.expected}
                          />
                        </td>
                        <td>{item.detail.actual}</td>
                        <td>{item.detail.expected}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </span>
            )
          }

          return (
            <span className={c} key={`${item.title}${item.date}`}>
              <span className={styles.eventItemTitle}>{item.title}</span>
              {item.description && (
                <span className={styles.eventItemDescription}>
                  {item.description}
                </span>
              )}
            </span>
          )
        })}
    </span>
  )
}

export function getCampaignsFromDate(
  date: Date,
  campaigns: Map<Date, any>,
): any[] {
  const nextCampaigns: any[] = []
  campaigns.forEach((campaign: any): void => {
    const selectedTS = dateToTimeStamp(date)
    const campaignStartTS = dateToTimeStamp(
      apiDateStringToDate(campaign.startDate),
    )
    const campaignEndTS = dateToTimeStamp(apiDateStringToDate(campaign.endDate))

    if (selectedTS < campaignStartTS) return
    if (selectedTS > campaignEndTS) return
    nextCampaigns.push(campaign)
  })

  return nextCampaigns
}

export default function Calendar(): React.ReactElement {
  let currency = ''
  const dispatch = useDispatch()
  const [day, setDay]: [any, any] = useState(null)
  const [mounted, setMounted] = useState(false)
  const selectedKpi = 'None'
  const filters = {
    events: true,
    anomalies: true,
    campaigns: true,
  }

  const metricsData = useSelector((s: any): any => s.metrics)
  const anomalyData = useSelector((s: any): any => s.anomalies)
  const { selected } = useSelector(
    (s: any): { selected: { currency: string } } => s.site,
  )

  if (selected) {
    const { currency: c } = selected
    currency = c
  }

  useEffect((): void => {
    dispatch(getCurrentKeyMetrics())
    // eslint-disable-next-line
  }, [])

  useEffect((): void => {
    if (metricsData.currentKeyMetrics.length > 0 && !mounted) {
      setMounted(true)
      metricsData.currentKeyMetrics.forEach((item: KeyMetrics) => {
        const { to, from } = getAnomalyDateRange('Year')
        const { metricID } = item
        dispatch(requestAnomalyGraphData({ to, from, metricID }))
      })
    }
    // eslint-disable-next-line
  }, [metricsData])

  useEffect((): void => {
    dispatch(
      getCalendarAnomalyRangeAsync({
        fromDate: formatForAPI(startOfYear()),
        toDate: formatForAPI(endOfMonth()),
      }),
    )
    dispatch(
      getCalendarCampaignsAsync({
        fromDate: formatForAPI(startOfYear()),
        toDate: formatForAPI(endOfMonth()),
      }),
    )
    dispatch(
      getCalendarEventsAsync({
        fromDate: formatForAPI(startOfYear()),
        toDate: formatForAPI(endOfMonth()),
      }),
    )
    dispatch(getCalendarKpiMetrics())
    dispatch(getCalendarGoalMetrics())
    // eslint-disable-next-line
  }, [])

  const { anomalies, events, campaigns, kpiMetrics, kpis } = useSelector(
    (s: any): any => s.calendar,
  )

  useEffect((): void => {
    if (kpiMetrics !== [] && selectedKpi !== 'None') {
      dispatch(
        getCalendarKpis({
          fromDate: formatForAPI(startOfYear()),
          toDate: formatForAPI(endOfMonth()),
          metric: selectedKpi,
        }),
      )
    }
    // eslint-disable-next-line
  }, [selectedKpi, kpiMetrics])

  const selectDate = (e: any): void => {
    const nextSelectedDayEvent = events.get(e.date.toString()) || null
    const nextSelectedDayAnomaly = anomalies.get(e.date.toString()) || null
    const nextSelectedDayCampaign = getCampaignsFromDate(
      e.date.toString(),
      campaigns,
    )
    setDay({
      date: e.date,
      events: filters.events ? nextSelectedDayEvent : null,
      anomalies: filters.anomalies ? nextSelectedDayAnomaly : null,
      campaigns:
        nextSelectedDayCampaign.length > 0 ? nextSelectedDayCampaign : null,
    })
    // setSideBarActive(true)
  }

  useEffect((): void => {
    if (day !== null) {
      selectDate(day)
    }
    // eslint-disable-next-line
  }, [filters, anomalies, events, kpis, selectedKpi, campaigns])

  const eventsForRendering = useMemo(
    (): any[] =>
      fillEvents({
        filters,
        anomalies,
        events,
        kpis,
        selectedKpi,
        campaigns,
        currency,
        anomalyData,
      }),
    [
      filters,
      anomalies,
      events,
      kpis,
      selectedKpi,
      campaigns,
      currency,
      anomalyData,
    ],
  )

  const keys = Object.keys(eventsForRendering)
  if (keys.length === 0) {
    return (
      <>
        <div className={styles.container}>
          <div className={styles.events}>
            <Preloader />
          </div>
        </div>
      </>
    )
  }
  return (
    <>
      <div className={styles.container}>
        <div className={styles.events}>
          {keys
            .sort()
            .reverse()
            .map((key) => {
              const items = eventsForRendering[key]
              return (
                <div key={`${key}`} className={styles.eventRow}>
                  <span className={styles.eventDate}>
                    {moment(key).format(dateFormatShort)}
                  </span>
                  <span className={styles.eventWrapper}>
                    <EventRow items={items} />
                  </span>
                </div>
              )
            })}
        </div>
      </div>
    </>
  )
}

// https://codesandbox.io/s/fullcalendar-react-3xpj8
